// src/pages/Appli/AppliAdmin/clubs-admin/hooks/useClubsAdmin.js

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  deleteClub,
  getClubs,
  getMembersByClub,
  updateClub,
} from "../../../../../services/clubServices";

import { useNotification } from "../../../../../components/Notification/NotificationManager";

import { CLUBS_PAGE_SIZE } from "../data/clubsAdmin.data";

import {
  getClubSearchText,
  getUniqueDepartments,
  normalizeText,
} from "../utils/clubsAdmin.utils";

import useDebouncedValue from "./useDebouncedValue";

const EMPTY_CLUB_MODAL = {
  isOpen: false,
  isEditMode: false,
  clubId: null,
};

const EMPTY_MEMBERS_MODAL = {
  isOpen: false,
  clubId: null,
  clubName: "",
};

function normalizeAdherentCount(value) {
  const numericValue = Number(value);

  if (
    !Number.isInteger(numericValue) ||
    numericValue < 0
  ) {
    return 0;
  }

  return numericValue;
}

export default function useClubsAdmin() {
  const { addNotification } = useNotification();

  const mountedRef = useRef(true);
  const memberCountRequestsRef = useRef(new Set());

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const [
    adherentCountsSavingByClub,
    setAdherentCountsSavingByClub,
  ] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(
    searchTerm,
    300,
  );

  const [
    selectedDepartement,
    setSelectedDepartement,
  ] = useState("");

  const [page, setPage] = useState(1);

  const [clubModal, setClubModal] = useState(
    EMPTY_CLUB_MODAL,
  );

  const [membersModal, setMembersModal] = useState(
    EMPTY_MEMBERS_MODAL,
  );

  /*
   * Nombre d'utilisateurs de l'application
   * liés à chaque club.
   *
   * À ne pas confondre avec nombre_adherents,
   * qui représente les licenciés administratifs.
   */
  const [
    memberCountsByClub,
    setMemberCountsByClub,
  ] = useState({});

  const [
    memberCountsLoadingByClub,
    setMemberCountsLoadingByClub,
  ] = useState({});

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchClubs = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getClubs();

      if (!mountedRef.current) return;

      setClubs(
        Array.isArray(data)
          ? data.map((club) => ({
              ...club,
              nombre_adherents: normalizeAdherentCount(
                club?.nombre_adherents,
              ),
            }))
          : [],
      );
    } catch (error) {
      console.error(
        "Erreur lors du chargement des clubs :",
        error,
      );

      if (mountedRef.current) {
        setClubs([]);

        addNotification(
          "Erreur lors du chargement des clubs",
          "error",
        );
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [addNotification]);

  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  const departements = useMemo(
    () => getUniqueDepartments(clubs),
    [clubs],
  );

  const filteredClubs = useMemo(() => {
    const normalizedSearch = normalizeText(
      debouncedSearch,
    );

    return clubs
      .filter((club) => {
        if (!selectedDepartement) return true;

        return (
          club?.departement_club ===
          selectedDepartement
        );
      })
      .filter((club) => {
        if (!normalizedSearch) return true;

        return normalizeText(
          getClubSearchText(club),
        ).includes(normalizedSearch);
      })
      .sort((firstClub, secondClub) =>
        String(
          firstClub?.nom_club || "",
        ).localeCompare(
          String(secondClub?.nom_club || ""),
          "fr",
          {
            sensitivity: "base",
          },
        ),
      );
  }, [
    clubs,
    debouncedSearch,
    selectedDepartement,
  ]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    selectedDepartement,
  ]);

  const totalClubs = filteredClubs.length;

  const totalDepartments = useMemo(
    () =>
      new Set(
        filteredClubs
          .map(
            (club) =>
              club?.departement_club,
          )
          .filter(Boolean),
      ).size,
    [filteredClubs],
  );

  const totalSites = useMemo(
    () =>
      filteredClubs.filter((club) =>
        String(
          club?.site_club || "",
        ).trim(),
      ).length,
    [filteredClubs],
  );

  const totalAdherents = useMemo(
    () =>
      filteredClubs.reduce(
        (total, club) =>
          total +
          normalizeAdherentCount(
            club?.nombre_adherents,
          ),
        0,
      ),
    [filteredClubs],
  );

  const pageCount = Math.max(
    1,
    Math.ceil(
      totalClubs / CLUBS_PAGE_SIZE,
    ),
  );

  const safePage = Math.min(
    page,
    pageCount,
  );

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [
    page,
    safePage,
  ]);

  const paginatedRows = useMemo(() => {
    const startIndex =
      (safePage - 1) *
      CLUBS_PAGE_SIZE;

    return filteredClubs.slice(
      startIndex,
      startIndex +
        CLUBS_PAGE_SIZE,
    );
  }, [
    filteredClubs,
    safePage,
  ]);

  /*
   * Chargement du nombre de membres de l'application
   * pour les clubs de la page affichée.
   */
  useEffect(() => {
    const clubIdsToLoad = paginatedRows
      .map((club) => club?.id)
      .filter(Boolean)
      .filter((clubId) => {
        const alreadyLoaded =
          Object.prototype.hasOwnProperty.call(
            memberCountsByClub,
            clubId,
          );

        const alreadyLoading =
          memberCountRequestsRef.current.has(
            clubId,
          );

        return (
          !alreadyLoaded &&
          !alreadyLoading
        );
      });

    if (clubIdsToLoad.length === 0) {
      return;
    }

    clubIdsToLoad.forEach(
      (clubId) => {
        memberCountRequestsRef.current.add(
          clubId,
        );
      },
    );

    setMemberCountsLoadingByClub(
      (previous) => {
        const next = {
          ...previous,
        };

        clubIdsToLoad.forEach(
          (clubId) => {
            next[clubId] = true;
          },
        );

        return next;
      },
    );

    Promise.allSettled(
      clubIdsToLoad.map(
        async (clubId) => {
          const members =
            await getMembersByClub(
              clubId,
            );

          return {
            clubId,
            count: Array.isArray(
              members,
            )
              ? members.length
              : 0,
          };
        },
      ),
    )
      .then((results) => {
        if (!mountedRef.current) {
          return;
        }

        setMemberCountsByClub(
          (previous) => {
            const next = {
              ...previous,
            };

            results.forEach(
              (result, index) => {
                const requestedClubId =
                  clubIdsToLoad[
                    index
                  ];

                if (
                  result.status ===
                  "fulfilled"
                ) {
                  next[
                    result.value.clubId
                  ] =
                    result.value.count;
                } else {
                  console.error(
                    `Impossible de charger le nombre de membres du club ${requestedClubId} :`,
                    result.reason,
                  );

                  next[
                    requestedClubId
                  ] = null;
                }
              },
            );

            return next;
          },
        );
      })
      .finally(() => {
        clubIdsToLoad.forEach(
          (clubId) => {
            memberCountRequestsRef.current.delete(
              clubId,
            );
          },
        );

        if (!mountedRef.current) {
          return;
        }

        setMemberCountsLoadingByClub(
          (previous) => {
            const next = {
              ...previous,
            };

            clubIdsToLoad.forEach(
              (clubId) => {
                delete next[
                  clubId
                ];
              },
            );

            return next;
          },
        );
      });
  }, [
    paginatedRows,
    memberCountsByClub,
  ]);

  const openCreate = useCallback(
    () => {
      setClubModal({
        isOpen: true,
        isEditMode: false,
        clubId: null,
      });
    },
    [],
  );

  const openEdit = useCallback(
    (clubId) => {
      setClubModal({
        isOpen: true,
        isEditMode: true,
        clubId,
      });
    },
    [],
  );

  const closeClubModal = useCallback(
    () => {
      setClubModal(
        EMPTY_CLUB_MODAL,
      );
    },
    [],
  );

  const openMembers = useCallback(
    (club) => {
      setMembersModal({
        isOpen: true,
        clubId: club?.id || null,
        clubName:
          club?.nom_club || "",
      });
    },
    [],
  );

  const closeMembersModal =
    useCallback(() => {
      setMembersModal(
        EMPTY_MEMBERS_MODAL,
      );
    }, []);

  const updateMemberCount = useCallback(
    (clubId, count) => {
      if (!clubId) return;

      const numericCount =
        Number(count);

      setMemberCountsByClub(
        (previous) => ({
          ...previous,
          [clubId]:
            Number.isFinite(
              numericCount,
            )
              ? Math.max(
                  0,
                  numericCount,
                )
              : null,
        }),
      );
    },
    [],
  );

  /*
   * Mise à jour rapide du nombre de licenciés.
   *
   * Retourne true si la sauvegarde réussit,
   * false en cas d'erreur.
   */
  const handleUpdateAdherentCount =
    useCallback(
      async (
        clubId,
        nextCount,
      ) => {
        const numericCount =
          Number(nextCount);

        if (
          !clubId ||
          !Number.isInteger(
            numericCount,
          ) ||
          numericCount < 0
        ) {
          addNotification(
            "Le nombre de licenciés doit être un entier positif ou nul.",
            "error",
          );

          return false;
        }

        try {
          setAdherentCountsSavingByClub(
            (previous) => ({
              ...previous,
              [clubId]: true,
            }),
          );

          const updatedClub =
            await updateClub(
              clubId,
              {
                nombre_adherents:
                  numericCount,
              },
            );

          if (
            !mountedRef.current
          ) {
            return false;
          }

          const serverCount =
            normalizeAdherentCount(
              updatedClub
                ?.nombre_adherents ??
                numericCount,
            );

          setClubs(
            (previous) =>
              previous.map(
                (club) =>
                  club.id ===
                  clubId
                    ? {
                        ...club,
                        nombre_adherents:
                          serverCount,
                      }
                    : club,
              ),
          );

          addNotification(
            "Nombre de licenciés mis à jour",
            "success",
          );

          return true;
        } catch (error) {
          console.error(
            "Erreur lors de la mise à jour du nombre de licenciés :",
            error,
          );

          if (
            mountedRef.current
          ) {
            addNotification(
              error?.response?.data
                ?.message ||
                "Impossible de mettre à jour le nombre de licenciés",
              "error",
            );
          }

          return false;
        } finally {
          if (
            mountedRef.current
          ) {
            setAdherentCountsSavingByClub(
              (previous) => {
                const next = {
                  ...previous,
                };

                delete next[
                  clubId
                ];

                return next;
              },
            );
          }
        }
      },
      [addNotification],
    );

  const handleDelete = useCallback(
    async (club) => {
      const clubId = club?.id;

      if (!clubId) return;

      const clubName =
        club?.nom_club ||
        `Club #${clubId}`;

      const confirmed =
        window.confirm(
          `Supprimer définitivement le club « ${clubName} » ?`,
        );

      if (!confirmed) return;

      try {
        setDeletingId(clubId);

        await deleteClub(clubId);

        if (!mountedRef.current) {
          return;
        }

        setClubs((previous) =>
          previous.filter(
            (currentClub) =>
              currentClub.id !==
              clubId,
          ),
        );

        setMemberCountsByClub(
          (previous) => {
            const next = {
              ...previous,
            };

            delete next[
              clubId
            ];

            return next;
          },
        );

        addNotification(
          "Club supprimé avec succès",
          "success",
        );
      } catch (error) {
        console.error(
          "Erreur lors de la suppression du club :",
          error,
        );

        if (mountedRef.current) {
          addNotification(
            "Erreur lors de la suppression du club",
            "error",
          );
        }
      } finally {
        if (mountedRef.current) {
          setDeletingId(null);
        }
      }
    },
    [addNotification],
  );

  const resetFilters = useCallback(
    () => {
      setSearchTerm("");
      setSelectedDepartement("");
      setPage(1);
    },
    [],
  );

  const hasActiveFilters = Boolean(
    searchTerm.trim() ||
      selectedDepartement,
  );

  return {
    state: {
      loading,
      deletingId,

      searchTerm,
      selectedDepartement,
      page: safePage,

      clubModal,
      membersModal,

      memberCountsByClub,
      memberCountsLoadingByClub,

      adherentCountsSavingByClub,
    },

    computed: {
      departements,
      debouncedSearch,
      filteredClubs,
      paginatedRows,

      totalClubs,
      totalDepartments,
      totalSites,
      totalAdherents,

      pageCount,
      hasActiveFilters,
    },

    actions: {
      setSearchTerm,
      setSelectedDepartement,
      setPage,

      fetchClubs,

      openCreate,
      openEdit,
      closeClubModal,

      openMembers,
      closeMembersModal,

      updateMemberCount,
      handleUpdateAdherentCount,
      handleDelete,
      resetFilters,
    },
  };
}